/**
 * LabelLine 组件
 * 从标签锚点到地图蓝点的引导线
 *
 * 设计规则：
 * - 水平段由 StudioLabel 内部渲染（在 name 行和 meta 行之间）
 * - 本组件仅渲染垂直段 + 蓝色圆点
 */

import React, { useMemo } from 'react';
import { ILabelPosition } from '../../hooks/useMapLabelPositions';
import './index.scss';

interface ILabelLineProps {
  /** 标签的锚点位置 (标签边缘的连接点) */
  labelPosition: ILabelPosition;
  /** badge 在地图上的像素位置 (蓝点位置) */
  badgePosition: { x: number; y: number; };
  /** 是否使用虚线样式 */
  dashed?: boolean;
}

const LabelLine: React.FC<ILabelLineProps> = ({
  labelPosition,
  badgePosition,
  dashed = false,
}) => {
  const lineData = useMemo(() => {
    const ay = labelPosition.anchorY;
    const bx = badgePosition.x;
    const by = badgePosition.y;

    const vLen = Math.abs(by - ay);

    // 如果距离太小就不画
    if (vLen < 3) return null;

    // 垂直段: from (bx, ay) to (bx, by)
    const vLeft = bx;
    const vTop = Math.min(ay, by);
    const vHeight = vLen;

    return {
      // vertical segment
      vLeft,
      vTop,
      vHeight,
      // whether to show vertical segment
      showV: vLen >= 2,
    };
  }, [labelPosition, badgePosition]);

  if (!lineData) {
    return null;
  }

  return (
    <>
      {/* Vertical segment */}
      {lineData.showV && (
        <div
          className={`dbd-label-line dbd-label-line--v${dashed ? ' dbd-label-line--dashed' : ''}`}
          style={{
            left: `${lineData.vLeft}px`,
            top: `${lineData.vTop}px`,
            width: '1px',
            height: `${lineData.vHeight}px`,
          }}
        />
      )}
    </>
  );
};

export default React.memo(LabelLine);
